import { apiUrls } from '@/constant/configure';
import api from '@/helpers/api_helper';
import { flatMap, sortBy, uniq, uniqBy } from 'lodash';
import qs from 'qs';

function makeSortedAndUniqTagList(tags: MallTagItem[]) {
    try {
        return uniqBy(
            sortBy(tags, (o) => !o.display),
            'name',
        );
    } catch (e) {
        throw e;
    }
}

const searchTags = async (mallId: string, searchString?: string, limit?: number) => {
    try {
        searchString = searchString || '';

        return await api.get(`/mall/${mallId}/tag/search?tagName=${searchString}`).then((r) => {
            const tagData: string[] = r.data?.tagList || [];

            return uniq(tagData) || [];
        });
    } catch (e) {
        console.error(e);
        return [];
    }
};

export type MallTagItem = { name: string; display: boolean };
const getAllTags = async (mallId: string): Promise<MallTagItem[] | void> => {
    try {
        const allTags = await api.get(`/mall/${mallId}/tag`).then((r) => {
            return r.data?.tagList || r.data;
        });

        return makeSortedAndUniqTagList(allTags);
    } catch (e) {
        throw e;
    }
};

const getProductsRelatedTag = async (
    mallId: string,
    tagName: string,
    queryParams?: {
        limit?: number;
        offset?: number;
    },
) => {
    try {
        const defaultQueryParams = {
            limit: 1,
            offset: 0,
        };

        queryParams = !!queryParams
            ? {
                  ...defaultQueryParams,
                  ...queryParams,
              }
            : { ...defaultQueryParams };

        const queryString = qs.stringify(queryParams);

        return await api
            .get(`/mall/${mallId}/tag/${tagName}/product${!!queryString ? `?${queryString}` : ''}`)
            .then((r) => {
                const data = r?.data || r;

                return {
                    list: data.productList,
                    total: data.totalCount || 0,
                };
            });
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            // todo: 해당 내용이 없는 경우, 404 반환 또는 빈 배열 리스트와 totalCount 0 을 반환해주는 것이 더 적절함, 서버 측과 협의하여 향후 조정 필요.
            if (e.status === 422 && e.data?.message === 'No data exists.') {
                return {
                    list: [],
                    total: 0,
                };
            }

            throw e;
        }
    }
};

const generateNewTag = async (mallId: string, tagName: string) => {
    try {
        await api.post(`/mall/${mallId}/tag/${tagName}`);
    } catch (e: any) {
        console.error(e);

        if (e.status === 409) {
            console.error('이미 같은 태그가 존재함!!');
        }
    }
};

const putAllTags = async (mallId: string, payload: MallTagItem[]) => {
    if (!payload) return;

    try {
        const processedPayload = makeSortedAndUniqTagList(payload);

        await api.put(`/mall/${mallId}/tag`, {
            tagInfo: processedPayload || [],
        });
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

const deleteTag = async (mallId: string, tagName: string) => {
    try {
        await api.delete(`/mall/${mallId}/tag/${tagName}`);
    } catch (e) {
        throw e;
    }
};

// const getCategories = async(mallId:string) => {
//     try {
//         return getAllTags(mallId).then(r => {
//             if (!r) return [];

//             return r.filter(o => o.display);
//         })
//     } catch (e) {
//         console.error(e);
//         return [];
//     }
// }

export const getCategories = async (type: string = 'MAIN', parentCategoryId?: string) => {
    try {
        return await api
            .get(`/common/category?type=${type}${parentCategoryId ? `&parentCategoryId=${parentCategoryId}` : ''}`, {
                baseURL: apiUrls.matching,
            })
            .then(
                (r) =>
                    (r.data?.items as {
                        categoryId: string;
                        parentCategoryId: string | null;
                        wholeCategoryId: string;
                        wholeCategoryName: string;
                        categoryName: string;
                        imageUrl: string;
                        sortOrder: number;
                    }[]) || [],
            );
    } catch (error) {
        throw error;
    }
};
// 세션 스토리지에 저장할 데이터 타입 정의
interface CachedCategoryData {
    data: Awaited<ReturnType<typeof getCategories>>;
    timestamp: number;
}

// 캐시 유효 시간 (10분 = 600,000 밀리초)
const CACHE_EXPIRATION_TIME = 10 * 60 * 1000;

// 캐시 유효성 검사 함수
const isCacheValid = (timestamp: number): boolean => {
    const currentTime = new Date().getTime();
    return currentTime - timestamp < CACHE_EXPIRATION_TIME;
};

export const getPickCategories = async () => {
    let categoriesJson = window?.sessionStorage?.getItem?.('pick_page_category');
    let categories: Awaited<ReturnType<typeof getCategories>>;
    if (!categoriesJson || !isCacheValid(JSON.parse(categoriesJson).timestamp)) {
        categories = await getCategories();
        window?.sessionStorage?.setItem?.(
            'pick_page_category',
            JSON.stringify({ data: categories, timestamp: new Date().getTime() }),
        );
    } else {
        const cachedData = JSON.parse(categoriesJson) as CachedCategoryData;
        categories = cachedData.data;
    }

    const livingCategories = [
        {
            categoryId: '',
            categoryName: '리빙',
            imageUrl: 'https://d1vb419ubgm46b.cloudfront.net/sourcing-category/main/living.png',
            parentCategoryId: '',
            sortOrder: 0,
            wholeCategoryId: '',
            wholeCategoryName: '',
        },
        ...categories?.filter?.((c) => !['패션', '악세사리', '애슬레저', '식품'].includes(c.categoryName)),
    ];
    const athleisureCategory = categories?.find?.((c) => c.categoryName === '애슬레저');
    const supplementCategory = categories?.find?.((c) => c.categoryName === '건강기능식품');
    const foodCategory = categories?.find?.((c) => c.categoryName === '식품');
    const fashionCategory = categories?.find?.((c) => c.categoryName === '패션');
    const accessoryCategory = categories?.find?.((c) => c.categoryName === '악세사리');

    // 애슬레저 카테고리 처리
    let athleisureCategoriesJson = window?.sessionStorage?.getItem?.('pick_page_athleisure_category');
    let athleisureCategories: Awaited<ReturnType<typeof getCategories>>;

    if (!athleisureCategoriesJson || !isCacheValid(JSON.parse(athleisureCategoriesJson).timestamp)) {
        athleisureCategories = await getCategories('SUB', athleisureCategory?.categoryId || '');
        athleisureCategories.unshift(athleisureCategory!);

        const cacheData: CachedCategoryData = {
            data: athleisureCategories,
            timestamp: new Date().getTime(),
        };

        window?.sessionStorage?.setItem?.('pick_page_athleisure_category', JSON.stringify(cacheData));
    } else {
        const cachedData = JSON.parse(athleisureCategoriesJson) as CachedCategoryData;
        athleisureCategories = cachedData.data;
    }

    // 건강기능식품 카테고리 처리
    let supplementCategoriesJson = window?.sessionStorage?.getItem?.('pick_page_supplement_category');
    let supplementCategories: Awaited<ReturnType<typeof getCategories>>;

    if (!supplementCategoriesJson || !isCacheValid(JSON.parse(supplementCategoriesJson).timestamp)) {
        supplementCategories = await getCategories('SUB', supplementCategory?.categoryId || '');
        supplementCategories.unshift(supplementCategory!);

        const cacheData: CachedCategoryData = {
            data: supplementCategories,
            timestamp: new Date().getTime(),
        };

        window?.sessionStorage?.setItem?.('pick_page_supplement_category', JSON.stringify(cacheData));
    } else {
        const cachedData = JSON.parse(supplementCategoriesJson) as CachedCategoryData;
        supplementCategories = cachedData.data;
    }

    // 식품 카테고리 처리
    let foodCategoriesJson = window?.sessionStorage?.getItem?.('pick_page_food_category');
    let foodCategories: Awaited<ReturnType<typeof getCategories>>;

    if (!foodCategoriesJson || !isCacheValid(JSON.parse(foodCategoriesJson).timestamp)) {
        foodCategories = await getCategories('SUB', foodCategory?.categoryId || '');
        foodCategories.unshift(foodCategory!);

        const cacheData: CachedCategoryData = {
            data: foodCategories,
            timestamp: new Date().getTime(),
        };

        window?.sessionStorage?.setItem?.('pick_page_food_category', JSON.stringify(cacheData));
    } else {
        const cachedData = JSON.parse(foodCategoriesJson) as CachedCategoryData;
        foodCategories = cachedData.data;
    }

    // 패션 카테고리 처리
    let fashionCategoriesJson = window?.sessionStorage?.getItem?.('pick_page_fashion_category');
    let fashionCategories: Awaited<ReturnType<typeof getCategories>>;

    if (!fashionCategoriesJson || !isCacheValid(JSON.parse(fashionCategoriesJson).timestamp)) {
        fashionCategories = await getCategories('SUB', fashionCategory?.categoryId || '');
        fashionCategories.unshift(fashionCategory!);

        const cacheData: CachedCategoryData = {
            data: fashionCategories,
            timestamp: new Date().getTime(),
        };

        window?.sessionStorage?.setItem?.('pick_page_fashion_category', JSON.stringify(cacheData));
    } else {
        const cachedData = JSON.parse(fashionCategoriesJson) as CachedCategoryData;
        fashionCategories = cachedData.data;
    }

    // 악세사리 카테고리 처리
    let accessoryCategoriesJson = window?.sessionStorage?.getItem?.('pick_page_accessory_category');
    let accessoryCategories: Awaited<ReturnType<typeof getCategories>>;

    if (!accessoryCategoriesJson || !isCacheValid(JSON.parse(accessoryCategoriesJson).timestamp)) {
        accessoryCategories = await getCategories('SUB', accessoryCategory?.categoryId || '');
        accessoryCategories.unshift(accessoryCategory!);

        const cacheData: CachedCategoryData = {
            data: accessoryCategories,
            timestamp: new Date().getTime(),
        };

        window?.sessionStorage?.setItem?.('pick_page_accessory_category', JSON.stringify(cacheData));
    } else {
        const cachedData = JSON.parse(accessoryCategoriesJson) as CachedCategoryData;
        accessoryCategories = cachedData.data;
    }

    return {
        categories,
        livingCategories,
        athleisureCategories,
        foodCategories,
        fashionCategories,
        accessoryCategories,
        supplementCategories,
    };
};

const categoryServices = {
    searchTags,
    getAllTags,
    // getCategories,
    deleteTag,
    generateNewTag,
    getProductsRelatedTag,
    putAllTags,
};

export {
    searchTags,
    getAllTags,
    // getCategories,
    deleteTag,
    generateNewTag,
    getProductsRelatedTag,
    putAllTags,
};

export default categoryServices;
